import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

/**
 * The sign-in gate.
 *
 * This is the whole of what an unauthenticated visitor may see: the application's
 * identity statement and a form. There is no register link, no password reset and no
 * user administration — the repository has no route for one and this surface does not
 * pretend otherwise (AUTH-04).
 *
 * Nothing financial is rendered here, and the gate never renders the shell's private
 * region: an unauthenticated visitor gets no skeleton, no placeholder and no cached
 * private content.
 *
 * Accessibility (UQ-06): labelled fields, a real submit button, the error announced in
 * a `role="alert"` region that takes focus when it appears, and the pending state
 * carried on the button (`aria-disabled` plus its own text) rather than by silently
 * doing nothing. Focus is never trapped: every step is an ordinary tab stop.
 */

export interface SignInGateProps {
  /** A previous session ended, rather than this being a first visit. */
  readonly expired: boolean;
  readonly pending: boolean;
  readonly error: string | undefined;
  /** The same owner's in-memory identifier, when there is one to offer back. */
  readonly defaultEmail: string | undefined;
  onSubmit(email: string, password: string): void;
}

export function SignInGate({ expired, pending, error, defaultEmail, onSubmit }: SignInGateProps) {
  const formId = useId();
  const [email, setEmail] = useState(defaultEmail ?? '');
  const [password, setPassword] = useState('');
  const emailRef = useRef<HTMLInputElement>(null);
  const alertRef = useRef<HTMLParagraphElement>(null);

  // The identifier the shell keeps in memory for the owner whose session ended. It is
  // a recovery offer, not a stored value: it exists only for this tab.
  useEffect(() => {
    if (defaultEmail !== undefined && defaultEmail !== '') setEmail(defaultEmail);
  }, [defaultEmail]);

  // A fresh gate starts on the first field, so the form is usable from the keyboard
  // without hunting for it.
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  useEffect(() => {
    if (error !== undefined) alertRef.current?.focus();
  }, [error]);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (pending) return;
    onSubmit(email, password);
  }

  return (
    <main className="shell">
      <header>
        <h1>Saldo</h1>
        <p className="subtitle">
          {expired ? 'Your session ended. Sign in to continue.' : 'Sign in to open your ledger.'}
        </p>
      </header>

      {expired ? (
        <p className="notice" role="status" data-testid="session-ended">
          Nothing from the previous session is kept on this device. Sign in again to continue.
        </p>
      ) : null}

      <section aria-labelledby={`${formId}-heading`}>
        <h2 id={`${formId}-heading`}>Sign in</h2>
        <form className="form-grid" onSubmit={handleSubmit} noValidate>
          <label htmlFor={`${formId}-email`}>Email</label>
          <input
            id={`${formId}-email`}
            ref={emailRef}
            name="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />

          <label htmlFor={`${formId}-password`}>Password</label>
          <input
            id={`${formId}-password`}
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          <button type="submit" aria-disabled={pending} data-testid="sign-in">
            {pending ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {error !== undefined ? (
          <p className="status-bad" role="alert" tabIndex={-1} ref={alertRef} data-testid="sign-in-error">
            {error}
          </p>
        ) : null}

        <p className="hint">
          Access is provisioned for you. There is no self-service sign-up.
        </p>
      </section>
    </main>
  );
}
